import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import BroadcastService from "../services/BroadcastService";

const STATUS_OPTIONS = ["all", "Scheduled", "Sent", "sent", "delivered", "read", "failed", "replied"];

function BroadcastReportView() {
  const navigate = useNavigate();
  const { broadcastId } = useParams();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [title, setTitle] = useState("Broadcast Report");
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [reconciling, setReconciling] = useState(false);

  async function fetchRuns() {
    const data = await BroadcastService.getBroadcastRuns(broadcastId);
    const safe = data || [];
    setRuns(safe);
    if (!selectedRunId && safe.length > 0) {
      setSelectedRunId(safe[0].id);
    }
  }

  async function fetchReport() {
    const data = await BroadcastService.getBroadcastReport(broadcastId, {
      runId: selectedRunId,
      status,
      search,
      limit: 200,
      offset: 0,
    });
    setRows(data || []);
    if (data && data.length > 0) {
      setTitle(data[0].broadcast_name || "Broadcast Report");
    }
  }

  async function handleReconcile() {
    setReconciling(true);
    try {
      const result = await BroadcastService.reconcileBroadcastReport(broadcastId);
      const runId = result?.runId || "";
      const data = await BroadcastService.getBroadcastRuns(broadcastId);
      setRuns(data || []);
      if (runId) setSelectedRunId(runId);
      const report = await BroadcastService.getBroadcastReport(broadcastId, {
        runId: runId || selectedRunId,
        status,
        search,
        limit: 200,
        offset: 0,
      });
      setRows(report || []);
    } finally {
      setReconciling(false);
    }
  }

  useEffect(() => {
    fetchRuns();
  }, [broadcastId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedRunId && runs.length > 0) return;
    fetchReport();
  }, [broadcastId, status, selectedRunId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">Broadcast Recipient Delivery Report</Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate("/broadcast/messages")}>Back</Button>
      </Paper>

      <Paper sx={{ p: 2, display: "flex", gap: 2 }}>
        <TextField
          size="small"
          label="Search contact/template"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label="Run"
          value={selectedRunId}
          onChange={(e) => setSelectedRunId(e.target.value)}
          sx={{ minWidth: 260 }}
        >
          {runs.map((run) => (
            <MenuItem key={run.id} value={run.id}>
              {new Date(run.created_at).toLocaleString()} ({run.status})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {STATUS_OPTIONS.map((item) => (
            <MenuItem key={item} value={item}>{item}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={fetchReport}>Search</Button>
        <Button
          variant="outlined"
          color="warning"
          disabled={reconciling}
          onClick={handleReconcile}
        >
          {reconciling ? "Rebuilding…" : "Rebuild report"}
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Broadcast Name</TableCell>
              <TableCell>Template Name</TableCell>
              <TableCell>Contact Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Meta Queue</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.broadcast_name || "-"}</TableCell>
                <TableCell>{row.template_name || "-"}</TableCell>
                <TableCell>{row.contact_name || "-"}</TableCell>
                <TableCell>{row.phone_number || "-"}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.status || "-"} />
                </TableCell>
                <TableCell>{row.initial_meta_status || "-"}</TableCell>
                <TableCell>{row.error_message ? `${row.error_code || ""} ${row.error_message}`.trim() : "-"}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No report data available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default BroadcastReportView;
